import { LOCALES } from './catalog'
import type { LocaleId } from './types'

export type PaymentProvider = 'razorpay' | 'stripe'

/**
 * Razorpay settles domestic Indian payments; everything else goes to Stripe.
 * Detection is server-side from the edge geo header — never from a client-supplied value.
 */
export function resolveProvider(country: string | null | undefined): PaymentProvider {
  return country?.toUpperCase() === 'IN' ? 'razorpay' : 'stripe'
}

/**
 * Whether a card processor is actually wired for `provider`. One pure predicate shared by the
 * server's config guards (which 503 when it is false) and the `/api/workspace` signal the UI reads
 * to choose between a Subscribe button and a calm "opens soon" note — so the button and the
 * checkout route can never disagree about whether billing is open.
 */
export function billingConfigured(
  provider: PaymentProvider,
  keys: {
    stripeSecretKey?: string | null
    razorpayKeyId?: string | null
    razorpayKeySecret?: string | null
  },
): boolean {
  return provider === 'razorpay'
    ? Boolean(keys.razorpayKeyId && keys.razorpayKeySecret)
    : Boolean(keys.stripeSecretKey)
}

export const PROVIDER_LABELS: Record<PaymentProvider, string> = {
  razorpay: 'Razorpay',
  stripe: 'Stripe',
}

/** Payment methods each provider surfaces, used to set expectations before checkout opens. */
export const PROVIDER_METHODS: Record<PaymentProvider, string[]> = {
  razorpay: ['UPI', 'Netbanking', 'Cards', 'Wallets'],
  stripe: ['Cards', 'Apple Pay', 'Google Pay', 'SEPA'],
}

/** Currencies Stripe is configured to settle. Razorpay handles INR only in this deployment. */
const STRIPE_CURRENCIES = new Set(['USD', 'EUR', 'GBP', 'AED', 'SGD', 'BRL'])
const RAZORPAY_CURRENCIES = new Set(['INR'])

export function providerSupportsCurrency(provider: PaymentProvider, currency: string): boolean {
  return provider === 'razorpay'
    ? RAZORPAY_CURRENCIES.has(currency)
    : STRIPE_CURRENCIES.has(currency)
}

/**
 * Countries that map onto each pricing region. Regions priced below parity are restricted to
 * their own countries; the rest fall back to Stripe in the nearest listed currency.
 */
const LOCALE_COUNTRIES: Record<LocaleId, string[]> = {
  in: ['IN'],
  br: ['BR'],
  ae: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM'],
  sg: ['SG', 'MY', 'ID', 'PH', 'TH', 'VN'],
  uk: ['GB', 'IE'],
  eu: [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
    'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK',
    'SI', 'ES', 'SE', 'CH',
  ],
  us: [],
}

/**
 * Regions priced below parity. Selecting one of these from outside its own countries would
 * buy the same product at a discount meant for a different market, so it is not allowed.
 */
const PARITY_RESTRICTED: LocaleId[] = LOCALES.filter((l) => l.pppMultiplier < 0.85).map((l) => l.id)

export function localeForCountry(country: string | null | undefined): LocaleId {
  if (!country) return 'us'
  const upper = country.toUpperCase()
  for (const [localeId, countries] of Object.entries(LOCALE_COUNTRIES)) {
    if (countries.includes(upper)) return localeId as LocaleId
  }
  return 'us'
}

/**
 * Razorpay requires a fixed number of cycles up front. Ten years of billing is far past any
 * realistic subscription life, so it behaves as open-ended without leaving the field unset.
 */
export const RAZORPAY_TOTAL_COUNT = { monthly: 120, annual: 10 } as const

export const PROVIDER_PERIOD = {
  razorpay: { monthly: 'monthly', annual: 'yearly' },
  stripe: { monthly: 'month', annual: 'year' },
} as const

/**
 * Cancellation is not symmetric between processors: Stripe can reverse a pending
 * cancellation, Razorpay cannot — a cancelled subscription there has to be recreated.
 * The UI reads this so it never offers a resume button that would fail.
 */
export const PROVIDER_SUPPORTS_RESUME: Record<PaymentProvider, boolean> = {
  razorpay: false,
  stripe: true,
}

/** Stripe hosts a full self-service portal; Razorpay has no equivalent for us to link to. */
export const PROVIDER_HAS_PORTAL: Record<PaymentProvider, boolean> = {
  razorpay: false,
  stripe: true,
}

/**
 * Stripe credits the unused part of the old plan and charges the new one pro rata, so a plan
 * change can take effect immediately. Razorpay has no proration: switching mid-cycle would
 * either waste what the customer already paid or hand them free time, so their changes are
 * scheduled for the next cycle instead.
 */
export const PROVIDER_PRORATES: Record<PaymentProvider, boolean> = {
  razorpay: false,
  stripe: true,
}

/** When a saved configuration change reaches the customer's bill. */
export type PlanChangeTiming = 'immediate' | 'next_cycle' | 'not_billed_yet'

export interface PlanChangePreview {
  timing: PlanChangeTiming
  /** Net amount due now after credits, in minor units. Only meaningful when prorating. */
  amountDueMinor?: number
  /** Credit for unused time on the old plan, in minor units, expressed as a positive number. */
  creditMinor?: number
  currency?: string
  /** ISO date the new price starts applying. */
  effectiveAt?: string | null
  /**
   * Unix seconds the quote was calculated against. Sent back on save so the customer is
   * charged the figure they were shown rather than one recomputed at confirmation time.
   */
  prorationDate?: number
}

export interface RegionCheck {
  ok: boolean
  detectedCountry: string | null
  suggestedLocale: LocaleId
  provider: PaymentProvider
  reason?: string
}

/**
 * Confirms the chosen pricing region is one the payer is actually entitled to.
 * Without this, anyone could select India's 0.4x pricing from any country.
 */
export function checkRegionEligibility(
  country: string | null | undefined,
  localeId: LocaleId,
): RegionCheck {
  const suggestedLocale = localeForCountry(country)
  const provider = resolveProvider(country)
  const detectedCountry = country ? country.toUpperCase() : null

  if (!PARITY_RESTRICTED.includes(localeId)) {
    return { ok: true, detectedCountry, suggestedLocale, provider }
  }

  const allowed = LOCALE_COUNTRIES[localeId] ?? []
  if (detectedCountry && allowed.includes(detectedCountry)) {
    return { ok: true, detectedCountry, suggestedLocale, provider }
  }

  const localeName = LOCALES.find((l) => l.id === localeId)?.name ?? localeId
  return {
    ok: false,
    detectedCountry,
    suggestedLocale,
    provider,
    reason: detectedCountry
      ? `${localeName} pricing is only available to customers billing from ${localeName}. We detected ${detectedCountry}.`
      : `${localeName} pricing requires a billing address in that region, which we could not confirm.`,
  }
}
